import React, { useState, useEffect, useCallback } from 'react';
import {
  RiAddLine, RiEditLine, RiDeleteBinLine, RiSearchLine,
  RiCloseLine, RiCheckLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import { PageHeader, FilterRow, FormGroup, ConfirmModal, EmptyState, Toast, LoadingScreen } from '../../components/shared/UI';
import {
  adminGetKanaList, adminCreateKana, adminUpdateKana, adminDeleteKana,
} from '../../api/admin.api';

// function useToast() {
//   const [msg, setMsg] = useState('');
//   const show = m => { setMsg(m); setTimeout(() => setMsg(''), 2500); };
//   return [msg, show];
// }

function useToast() {
  const [msg, setMsg] = useState('');

  const show = useCallback((m) => {
    setMsg(m);
    setTimeout(() => setMsg(''), 2500);
  }, []);

  return [msg, show];
}

function Spinner({ label }) {
  return <LoadingScreen label={label} />;
}

const EMPTY = { type:'hiragana', kana:'', romaji:'', group:'vowels', order:'' };
const GROUPS = ['vowels','k','s','t','n','h','m','y','r','w','special'];
const GROUP_LABELS = { vowels:'Vowels', k:'K row', s:'S row', t:'T row', n:'N row', h:'H row', m:'M row', y:'Y row', r:'R row', w:'W row', special:'Special' };

export default function KanaManager() {
  const [list,    setList]    = useState([]);
  const [search,  setSearch]  = useState('');
  const [fType,   setFType]   = useState('All');
  const [fGroup,  setFGroup]  = useState('All');
  const [modal,   setModal]   = useState(false);
  const [form,    setForm]    = useState(EMPTY);
  const [editId,  setEditId]  = useState(null);
  const [delId,   setDelId]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   showToast]  = useToast();

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const params = { limit: 200 };

      if (fType !== 'All') params.type = fType.toLowerCase();
      if (fGroup !== 'All') params.group = fGroup;

      const res = await adminGetKanaList(params);
      setList(res.data);
    } catch (err) {
      showToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [fType, fGroup, showToast]);
  useEffect(() => {
    load();
  }, [load]);

  const filtered = search
    ? list.filter(k => k.kana.includes(search) || k.romaji.toLowerCase().includes(search.toLowerCase()))
    : list;

  const openEdit = k => {
    setForm({ type:k.type, kana:k.kana, romaji:k.romaji, group:k.group, order:String(k.order||'') });
    setEditId(k._id);
    setModal(true);
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]:v }));

  const save = async () => {
    if (!form.kana || !form.romaji) return;
    setSaving(true);
    const body = { ...form, order: parseInt(form.order) || 0 };
    try {
      if (editId) {
        const res = await adminUpdateKana(editId, body);
        setList(l => l.map(k => k._id === editId ? res.data : k));
        showToast('Updated!');
      } else {
        const res = await adminCreateKana(body);
        setList(l => [...l, res.data].sort((a,b) => (a.order||0) - (b.order||0)));
        showToast('Added!');
      }
      setModal(false);
    } catch (err) { showToast(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminDeleteKana(delId);
      setList(l => l.filter(k => k._id !== delId));
      showToast('Deleted.');
    } catch (err) { showToast(err.message); }
    finally { setDelId(null); }
  };

  if (loading) return <Spinner />;

  const hiraganaCount = list.filter(k => k.type === 'hiragana').length;
  const katakanaCount = list.filter(k => k.type === 'katakana').length;

  return (
    <div style={{ padding:32 }} className="anim-fade-up">
      <PageHeader
        title="Kana Manager"
        sub={`${hiraganaCount} hiragana · ${katakanaCount} katakana`}
        action={
          <button className="btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setModal(true); }}>
            <RiAddLine />Add Character
          </button>
        }
      />

      <FilterRow>
        <div className="search-wrap" style={{ flex:1, minWidth:180 }}>
          <RiSearchLine size={15} />
          <input className="ks-input" placeholder="Search kana or romaji…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="ks-select" style={{ width:130 }} value={fType} onChange={e => setFType(e.target.value)}>
          <option>All</option>
          <option value="Hiragana">Hiragana</option>
          <option value="Katakana">Katakana</option>
        </select>
        <select className="ks-select" style={{ width:120 }} value={fGroup} onChange={e => setFGroup(e.target.value)}>
          <option>All</option>
          {GROUPS.map(g => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}
        </select>
        <span style={{ fontSize:13, color:C.muted }}>{filtered.length} results</span>
      </FilterRow>

      <div className="ks-card" style={{ overflow:'hidden' }}>
        {filtered.length === 0
          ? <EmptyState icon={RiSearchLine} title="No characters found" sub="Add your first kana character above." />
          : (
            <div style={{ overflowX:'auto' }}>
              <table className="ks-table">
                <thead>
                  <tr>
                    <th>Character</th>
                    <th>Romaji</th>
                    <th>Type</th>
                    <th>Group</th>
                    <th>Order</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(k => (
                    <tr key={k._id}>
                      <td style={{ fontFamily:"'Noto Sans JP',sans-serif", fontSize:32, fontWeight:900, lineHeight:1 }}>{k.kana}</td>
                      <td style={{ fontWeight:700, fontSize:16 }}>{k.romaji}</td>
                      <td>
                        <span style={{
                          fontSize:11, fontWeight:700, borderRadius:6, padding:'3px 9px',
                          background: k.type === 'hiragana' ? `${C.blue}18` : `${C.purple}18`,
                          color: k.type === 'hiragana' ? C.blue : C.purple,
                          border: `1px solid ${k.type === 'hiragana' ? `${C.blue}35` : `${C.purple}35`}`,
                        }}>
                          {k.type.charAt(0).toUpperCase() + k.type.slice(1)}
                        </span>
                      </td>
                      <td style={{ fontSize:13, color:C.muted }}>{GROUP_LABELS[k.group] || k.group}</td>
                      <td style={{ fontWeight:700, textAlign:'center' }}>{k.order}</td>
                      <td style={{ fontSize:11, color:C.faint }}>{new Date(k.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn-icon" onClick={() => openEdit(k)}><RiEditLine size={14} /></button>
                          <button className="btn-icon danger" onClick={() => setDelId(k._id)}><RiDeleteBinLine size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:22 }}>
              <h2 style={{ fontSize:18, fontWeight:900 }}>{editId ? 'Edit Character' : 'Add Character'}</h2>
              <button className="btn-icon" onClick={() => setModal(false)}><RiCloseLine size={16} /></button>
            </div>

            <div className="ks-grid-2" style={{ gap:12 }}>
              <FormGroup label="Type">
                <select className="ks-select" value={form.type} onChange={e => f('type', e.target.value)}>
                  <option value="hiragana">Hiragana</option>
                  <option value="katakana">Katakana</option>
                </select>
              </FormGroup>
              <FormGroup label="Group">
                <select className="ks-select" value={form.group} onChange={e => f('group', e.target.value)}>
                  {GROUPS.map(g => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}
                </select>
              </FormGroup>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px', gap:12 }}>
              <FormGroup label="Character *">
                <input
                  className="ks-input"
                  placeholder="e.g. あ"
                  value={form.kana}
                  onChange={e => f('kana', e.target.value)}
                  style={{ fontFamily:"'Noto Sans JP',sans-serif", fontSize:28, textAlign:'center', padding:8 }}
                />
              </FormGroup>
              <FormGroup label="Romaji *">
                <input
                  className="ks-input"
                  placeholder="e.g. a"
                  value={form.romaji}
                  onChange={e => f('romaji', e.target.value)}
                  style={{ fontWeight:700, fontSize:16 }}
                />
              </FormGroup>
              <FormGroup label="Order">
                <input
                  className="ks-input"
                  type="number"
                  placeholder="1"
                  value={form.order}
                  onChange={e => f('order', e.target.value)}
                />
              </FormGroup>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <button className="btn-ghost" style={{ flex:1, justifyContent:'center' }} onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={save} disabled={saving}>
                {saving ? <div className="spinner" /> : <><RiCheckLine />{editId ? 'Save' : 'Add'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delId && (
        <ConfirmModal
          message="This kana character will be permanently deleted."
          onConfirm={handleDelete}
          onCancel={() => setDelId(null)}
        />
      )}

      <Toast message={toast} />
    </div>
  );
}
